import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Select,
  Space,
  Descriptions,
  Row,
  Col,
  Radio,
  Switch,
  Typography,
  TimePicker,
  Button,
  Form,
  Divider,
  message as antdMsg,
  Skeleton
} from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import moment from 'moment';
import ContentCustom from '../../../components/ContentCustom/ContentCustom';
import PageHeader from '../../../components/PageHeader/PageHeader';
import useAuthContext from '../../../contexts/AuthContext';
import useErrorMessage from '../../../utils/ErrorMessage';
import {
  formValues,
  contract,
  documents,
  payment,
  sinister,
  compta
} from './utils';

const Clients = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { user, dispatchAPI } = useAuthContext();
  const { message } = useErrorMessage();
  const [productsList, setProductsList] = useState([]);
  const [loading, setIsLoading] = useState(false);

  const format = 'HH:mm';

  const days = [
    'mondayMorning',
    'mondayAfternoon',
    'tuesdayMorning',
    'tuesdayAfternoon',
    'wenesdayMorning',
    'wenesdayAfternoon',
    'thursdayMorning',
    'thursdayAfternoon',
    'fridayMorning',
    'fridayAfternoon',
    'saturdayMorning',
    'saturdayAfternoon'
  ];

  const getSwitchFormItem = (resName, label, name) => (
    <Descriptions.Item key={name}>
      <Form.Item className="clients-access-switch">
        <Form.Item noStyle name={[name]} valuePropName="checked">
          <Switch
            checkedChildren={t('settings.clientMenu.yes')}
            unCheckedChildren={t('settings.clientMenu.no')}
          />
        </Form.Item>
        <span style={{ marginLeft: 10 }}>{t(`${resName}.${label}`)}</span>
      </Form.Item>
    </Descriptions.Item>
  );

  const generatePolicesPanelV2 = (collection, choices) => {
    return (
      <>
        <Descriptions.Item>
          <div style={{ display: 'block', width: '100%' }}>
            <Divider orientation="left" plain>
              <Typography.Paragraph>
                {t(`settings.clientMenu.contractInfoMsg.${collection}.title`)}
              </Typography.Paragraph>
            </Divider>
            {choices?.map((item) => (
              <Row key={item}>
                <Col xs={24} lg={10} style={{ marginBottom: 8 }}>
                  {t(
                    `settings.clientMenu.contractInfoMsg.${collection}.${item}`
                  )}
                </Col>
                <Col xs={24} lg={14}>
                  <Form.Item name={[collection, item]}>
                    <Radio.Group>
                      <Space orientation="vertical">
                        <Radio value="allow" style={{ whiteSpace: 'normal' }}>
                          {t(
                            `settings.clientMenu.contractInfoMsg.${collection}.allow`
                          )}
                        </Radio>
                        {collection !== 'contract' ? (
                          <Radio value="alert" style={{ whiteSpace: 'normal' }}>
                            {t(
                              `settings.clientMenu.contractInfoMsg.${collection}.alert`
                            )}
                          </Radio>
                        ) : (
                          ''
                        )}
                        <Radio value="hide" style={{ whiteSpace: 'normal' }}>
                          {t(
                            `settings.clientMenu.contractInfoMsg.${collection}.hide`
                          )}
                        </Radio>
                      </Space>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
            ))}
          </div>
        </Descriptions.Item>
      </>
    );
  };

  const getProductsList = () => {
    const children = [];
    if (productsList)
      productsList.forEach((item) => {
        children.push(
          <Select.Option value={item} key={item}>
            {item}
          </Select.Option>
        );
      });
    return children;
  };

  const handleSubmit = async (body) => {
    setIsLoading(true);
    try {
      await dispatchAPI('PATCH', {
        url: `/customerPermissions/${user?.insurer?.customer_permissions}`,
        body
      });
      antdMsg.success('Configuration enregistrée');
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsLoading(false);
  };

  const getProductsListApi = async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/customerPermissions/productsEnums`
      });
      setProductsList(data);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const getUser = async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/customerPermissions/${user?.insurer?.customer_permissions}`
      });
      form.setFieldsValue(data);
      for (let i = 0; i < days.length; i += 1) {
        if (data.openHours[days[i]].length) {
          form.setFieldsValue({
            openHours: {
              [days[i]]: [
                moment(data.openHours[days[i]][0]),
                moment(data.openHours[days[i]][1])
              ]
            }
          });
        }
      }
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await getProductsListApi();
      await getUser();
      setIsLoading(false);
    })();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <PageHeader title={user?.insurer?.name} />
      <ContentCustom>
        <Skeleton loading={loading}>
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              label={t('settings.clientMenu.clientAccessMsg')}
              name={['clientAccess']}
              valuePropName="checked"
            >
              <Switch
                checkedChildren={t('settings.clientMenu.yes')}
                unCheckedChildren={t('settings.clientMenu.no')}
              />
            </Form.Item>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) =>
                getFieldValue('clientAccess') && (
                  <Form.Item
                    label={t('settings.clientMenu.advParamsMsg')}
                    name={['advAccess']}
                    style={{ marginBottom: 40 }}
                    valuePropName="checked"
                  >
                    <Switch
                      checkedChildren={t('settings.clientMenu.yes')}
                      unCheckedChildren={t('settings.clientMenu.no')}
                    />
                  </Form.Item>
                )
              }
            </Form.Item>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) =>
                getFieldValue('clientAccess') &&
                getFieldValue('advAccess') && (
                  <>
                    <div className="clients-access-wrapper">
                      <Typography.Title level={4}>
                        {t('settings.clientMenu.companyInfo.title')}
                      </Typography.Title>
                      <Descriptions>
                        {formValues.map((item) =>
                          getSwitchFormItem(
                            'settings.clientMenu.companyInfo',
                            item,
                            item
                          )
                        )}
                      </Descriptions>
                    </div>
                    <Descriptions>
                      <div
                        style={{ marginTop: 20 }}
                        className="clients-access-time"
                      >
                        <Typography.Title level={5}>
                          {t('settings.clientMenu.companyInfo.openHours')}
                        </Typography.Title>
                        <Descriptions.Item>
                          {days.map((day) => (
                            <>
                              <Form.Item
                                key={day}
                                label={t(`settings.clientMenu.days.${day}`)}
                                name={['openHours', day]}
                                style={{ margin: '10px 0px 10px 10px' }}
                              />
                              <TimePicker.RangePicker
                                format={format}
                                style={{
                                  minWidth: 270,
                                  margin: '-15px 0px 0px 0px'
                                }}
                              />
                            </>
                          ))}
                        </Descriptions.Item>
                      </div>
                    </Descriptions>
                    <div>
                      <Typography.Title level={4}>
                        {t('settings.clientMenu.companyProductsMsg.title')}
                      </Typography.Title>
                      <Descriptions>
                        <Descriptions.Item>
                          <div style={{ width: '100%' }}>
                            <Form.Item
                              label={t(
                                'settings.clientMenu.companyProductsMsg.products'
                              )}
                              name="products"
                            >
                              <Select
                                mode="tags"
                                style={{ width: '100%' }}
                                placeholder="Sélectionnez vos produits"
                              >
                                {getProductsList()}
                              </Select>
                            </Form.Item>
                          </div>
                        </Descriptions.Item>
                      </Descriptions>
                    </div>
                    <div>
                      <Typography.Title level={4}>
                        {t('settings.clientMenu.contractInfoMsg.title')}
                      </Typography.Title>
                      <Descriptions
                        column={1}
                        className="clients-contract-access"
                      >
                        <div>
                          {generatePolicesPanelV2('contract', contract)}
                        </div>
                        <div>
                          {generatePolicesPanelV2('documents', documents)}
                        </div>
                        <div>{generatePolicesPanelV2('payment', payment)}</div>
                        <div>
                          {generatePolicesPanelV2('sinister', sinister)}
                        </div>
                        <div>{generatePolicesPanelV2('compta', compta)}</div>
                      </Descriptions>
                    </div>
                  </>
                )
              }
            </Form.Item>
            <Row justify="end" style={{ marginTop: 24 }}>
              <Button type="add" htmlType="submit">
                {t('buttons.save')}
                <CheckOutlined />
              </Button>
            </Row>
          </Form>
        </Skeleton>
      </ContentCustom>
    </>
  );
};

export default Clients;
